import React, { useEffect, useRef, useState } from "react";
import "./Experience.css";

import {
    BsBook,
    BsFillGearFill,
    BsFileEarmarkCheck,
    BsFillFileEarmarkMedicalFill,
    BsPersonVcard,
    BsMap,
    BsPersonBoundingBox,
    BsDownload,
    BsChevronDoubleUp,
    BsChevronDown
} from "react-icons/bs";
import { Box, Typography, Button, Paper, List, ListItem, Divider } from "@mui/material";
import LazyImage from "./LazyImage";

import jobs from "./data/jobs.json";
import certificates from "./data/certificates.json";

function formatMonthYear(ym) {
    if (!ym) return "Present";
    const [y, m] = ym.split("-").map(Number);
    const d = new Date(y, (m || 1) - 1, 1);
    return d.toLocaleString(undefined, { month: "long", year: "numeric" });
}

const quickSkills = [
    "React",
    "TypeScript",
    "JavaScript",
    "Node.js",
    "Python",
    "MySQL",
    "MongoDB",
    "Material UI",
    "Tailwind",
    "Git"
];

function SectionTitle({ icon, children }) {
    return (
        <Typography className="resume-section-title" variant="h5">
            {icon}
            <Box component="span">{children}</Box>
        </Typography>
    );
}

function Experience() {
    const [highlightedSection, setHighlightedSection] = useState("");
    const [showResumeTopButton, setShowResumeTopButton] = useState(false);
    const [expandedJobIds, setExpandedJobIds] = useState([]);
    const resumeMainRef = useRef(null);
    const summaryRef = useRef(null);
    const experienceRef = useRef(null);
    const educationRef = useRef(null);
    const certificatesRef = useRef(null);
    const highlightTimeoutRef = useRef(null);

    const sectionNav = [
        { label: "Summary", icon: <BsPersonVcard />, ref: summaryRef },
        { label: "Experience", icon: <BsFillFileEarmarkMedicalFill />, ref: experienceRef },
        { label: "Education", icon: <BsBook />, ref: educationRef },
        { label: "Certificates", icon: <BsFileEarmarkCheck />, ref: certificatesRef }
    ];

    const handleDownload = () => {
        const link = document.createElement("a");
        link.href = "/files/Dimostenis_Nikolakopoulos_Resume.pdf";
        link.download = "Dimostenis_Nikolakopoulos_Resume.pdf";
        link.click();
    };

    useEffect(() => {
        return () => {
            if (highlightTimeoutRef.current) {
                clearTimeout(highlightTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const resumeMain = resumeMainRef.current;
        if (!resumeMain) return;

        const desktopQuery = window.matchMedia("(min-width: 1051px)");

        const handleScroll = () => {
            const maxScroll = resumeMain.scrollHeight - resumeMain.clientHeight;
            setShowResumeTopButton(desktopQuery.matches && maxScroll > 0 && resumeMain.scrollTop / maxScroll > 0.4);
        };

        handleScroll();
        resumeMain.addEventListener("scroll", handleScroll);
        desktopQuery.addEventListener("change", handleScroll);

        return () => {
            resumeMain.removeEventListener("scroll", handleScroll);
            desktopQuery.removeEventListener("change", handleScroll);
        };
    }, []);

    const scrollResumeToTop = () => {
        resumeMainRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    };

    const toggleJob = (jobId) => {
        setExpandedJobIds((current) =>
            current.includes(jobId)
                ? current.filter((id) => id !== jobId)
                : [...current, jobId]
        );
    };

    const scrollToSection = (sectionRef, label) => {
        sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

        if (highlightTimeoutRef.current) {
            clearTimeout(highlightTimeoutRef.current);
        }

        setHighlightedSection(label);

        highlightTimeoutRef.current = setTimeout(() => {
            setHighlightedSection("");
        }, 3500);
    };

    return (
        <Box id="experienceContainer" className="fade-in">
            <Box className="resume-dashboard">
                <aside className="resume-rail">
                    <Box className="profile-card">
                        <LazyImage src="./assets/meMostRectent.png" alt="Dimostenis Nikolakopoulos" className="profile-photo" />
                        <Typography component="h2" variant="h5">Dimostenis Nikolakopoulos</Typography>
                        <Typography variant="body2">Software Developer</Typography>
                        <Button
                            variant="contained"
                            startIcon={<BsDownload />}
                            onClick={handleDownload}
                            className="resume-download"
                        >
                            Download Resume
                        </Button>
                    </Box>

                    <Box className="resume-rail-scroll">
                        <Box className="rail-card rail-card-blue">
                            <SectionTitle icon={<BsMap />}>Location</SectionTitle>
                            <Typography variant="body2">
                                Primarily based in the Northern New Jersey / NYC Metro Area; temporarily residing in Athens, Greece.
                            </Typography>
                        </Box>

                        <Box className="rail-card">
                            <SectionTitle icon={<BsPersonBoundingBox />}>Work Clearance</SectionTitle>
                            <Typography variant="body2">
                                United States citizen, fully authorized to work in the US, and does not require sponsorship.
                            </Typography>
                        </Box>

                        <Box className="rail-card">
                            <SectionTitle icon={<BsFillGearFill />}>Quick Skills</SectionTitle>
                            <Box className="skill-cloud">
                                {quickSkills.map((skill) => (
                                    <Box key={skill} component="span" className="skill-pill">{skill}</Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </aside>

                <Box className="resume-content-shell">
                    <main className="resume-main" ref={resumeMainRef}>
                        <section
                            ref={summaryRef}
                            className={`resume-panel resume-summary ${highlightedSection === "Summary" ? "resume-section-highlight" : ""}`}
                        >
                            <SectionTitle icon={<BsPersonVcard />}>Summary</SectionTitle>
                            <Typography variant="body1">
                                Passionate and results-driven Software Developer with over 5 years of comprehensive experience in full-stack development. Seeking to leverage expertise in software engineering to contribute to innovative projects, particularly in autonomous systems, low-code development platforms, and online platforms.
                            </Typography>
                            <Typography variant="body1">
                                Innovative Web Developer and ReactJS Specialist | Crafting Dynamic User Experiences | Passionate about Modern Web Technologies and Interactive Design
                            </Typography>
                        </section>

                        <section
                            ref={experienceRef}
                            className={`resume-panel resume-panel-blue ${highlightedSection === "Experience" ? "resume-section-highlight" : ""}`}
                        >
                            <SectionTitle icon={<BsFillFileEarmarkMedicalFill />}>Professional Experience</SectionTitle>
                            <Box className="job-list">
                                {jobs.map((job) => {
                                    const expanded = expandedJobIds.includes(job.id);

                                    return (
                                    <Paper
                                        key={job.id}
                                        elevation={0}
                                        variant="outlined"
                                        className={`job-card ${expanded ? "job-card-expanded" : ""}`}
                                    >
                                        <Box className="job-header">
                                            <Box>
                                                <Typography variant="overline">{job.company}</Typography>
                                                <Typography component="h3" variant="h6">{job.title}</Typography>
                                            </Box>
                                            <LazyImage src={job.logo} alt={job.logoAlt} className="job-logo" />
                                        </Box>

                                        <Typography variant="body2" className="job-meta">
                                            <Box component="span">{job.location}</Box>
                                            <Box component="span">/</Box>
                                            <Box component="span">{formatMonthYear(job.start)} - {formatMonthYear(job.end)}</Box>
                                        </Typography>

                                        <button
                                            type="button"
                                            className="job-expand-button"
                                            onClick={() => toggleJob(job.id)}
                                            aria-expanded={expanded}
                                        >
                                            <span>{expanded ? "Hide details" : "View details"}</span>
                                            <BsChevronDown />
                                        </button>

                                        <Box className="job-details">
                                            <Divider sx={{ my: 1.25 }} />

                                            <List dense disablePadding className="job-bullets">
                                                {job.bullets.map((bullet, index) => (
                                                    <ListItem key={index} disableGutters>
                                                        <Typography variant="body2">{bullet}</Typography>
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                    </Paper>
                                    );
                                })}
                            </Box>
                        </section>

                        <section
                            ref={educationRef}
                            className={`resume-panel education-panel ${highlightedSection === "Education" ? "resume-section-highlight" : ""}`}
                        >
                            <SectionTitle icon={<BsBook />}>Education</SectionTitle>
                            <Box className="education-row">
                                <Box>
                                    <Typography variant="h6">Bachelor of Science in Computer Science</Typography>
                                    <Typography variant="body2">New Jersey Institute of Technology (NJIT), Newark, NJ, USA</Typography>
                                    <Typography variant="body2">Graduated May 2021</Typography>
                                </Box>
                                <LazyImage src="./assets/njit.png" alt="NJIT" className="education-logo" />
                            </Box>
                        </section>

                        <section
                            ref={certificatesRef}
                            className={`resume-panel ${highlightedSection === "Certificates" ? "resume-section-highlight" : ""}`}
                        >
                            <SectionTitle icon={<BsFileEarmarkCheck />}>Certificates</SectionTitle>
                            <Box className="certificate-grid">
                                {certificates.map((cert) => (
                                    <Box key={cert.id} className="certificate-card">
                                        <Typography variant="h6">{cert.title}</Typography>
                                        <List dense disablePadding>
                                            {cert.courses.map((course, index) => (
                                                <ListItem key={index} disableGutters>
                                                    <Typography variant="body2">{course}</Typography>
                                                </ListItem>
                                            ))}
                                        </List>
                                        <Button
                                            component="a"
                                            href={cert.certificateUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            variant="outlined"
                                            size="small"
                                        >
                                            View Certificate
                                        </Button>
                                    </Box>
                                ))}
                            </Box>
                        </section>
                    </main>

                    {showResumeTopButton && (
                        <button
                            type="button"
                            onClick={scrollResumeToTop}
                            className="resume-back-to-top"
                            title="Go to top"
                            aria-label="Go to top of resume"
                        >
                            <BsChevronDoubleUp />
                        </button>
                    )}

                    <nav className="resume-section-nav" aria-label="Resume sections">
                        {sectionNav.map((item) => (
                            <button
                                key={item.label}
                                type="button"
                                className={`resume-section-nav-button ${highlightedSection === item.label ? "resume-section-nav-button-active" : ""}`}
                                aria-current={highlightedSection === item.label ? "true" : undefined}
                                onClick={() => scrollToSection(item.ref, item.label)}
                            >
                                <span className="resume-section-nav-icon">{item.icon}</span>
                                <span>{item.label}</span>
                            </button>
                        ))}
                    </nav>
                </Box>
            </Box>
        </Box>
    );
}

export default Experience;
